// CreateTemplateModal.jsx (now supports both create and edit)
import { useRef, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { createTemplate, updateTemplate } from "../../../services/templateServices";
import {
  Plus,
  Minus,
  X,
  FileText,
  Image,
  MessageSquare,
  Settings,
  Hash,
  Type,
  File,
} from "lucide-react";

const CreateTemplateModal = ({ onClose, editData = null }) => {
  const modalRef = useRef(null);
  const isEditing = Boolean(editData);

  const [placeholders, setPlaceholders] = useState([
    { position: 1, label: "" },
  ]);

  const [templateId, setTemplateId] = useState("");
  const [templateName, setTemplateName] = useState("");
  const [sampleMessage, setSampleMessage] = useState("");
  const [type, setType] = useState("text");
  const [messageMode, setMessageMode] = useState("static");
  const [mediaUrl, setMediaUrl] = useState("");

  useEffect(() => {
    if (editData) {
      setTemplateId(editData.metaTemplateId || "");
      setTemplateName(editData.templateName || "");
      setSampleMessage(editData.sampleMessage || "");
      setType(editData.type || "text");
      setMediaUrl(editData.mediaUrl || "");
      
      if (editData.placeholders && editData.placeholders.length > 0) {
        setMessageMode("dynamic");
        setPlaceholders(editData.placeholders);
      } else {
        setMessageMode("static");
        setPlaceholders([{ position: 1, label: "" }]);
      }
    }
  }, [editData]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  const handleAddPlaceholder = () => {
    const nextPosition =
      placeholders.length > 0
        ? Math.max(...placeholders.map((p) => p.position)) + 1
        : 1;
    setPlaceholders([...placeholders, { position: nextPosition, label: "" }]);
  };

  const handleRemovePlaceholder = (index) => {
    const updated = [...placeholders];
    updated.splice(index, 1);
    setPlaceholders(updated);
  };

  const handlePlaceholderChange = (index, value) => {
    const updated = [...placeholders];
    updated[index].label = value;
    setPlaceholders(updated);
  };

  const handleSubmit = async () => {
    if (!templateId.trim() || !templateName.trim()) {
      toast.error("Template ID and Name are required");
      return;
    }

    if (
      messageMode === "dynamic" &&
      placeholders.some((p) => !p.label.trim())
    ) {
      toast.error("All placeholders must have labels");
      return;
    }

    const payload = {
      metaTemplateId: templateId.trim(),
      templateName: templateName.trim(),
      sampleMessage: sampleMessage.trim(),
      placeholders: messageMode === "dynamic" ? placeholders : [],
      type,
      mediaUrl: type === "media" ? mediaUrl : "",
    };

    const operation = isEditing 
      ? updateTemplate(editData._id, payload)
      : createTemplate(payload);

    toast
      .promise(operation, {
        loading: isEditing ? "Updating template..." : "Creating template...",
        success: isEditing ? "Template updated successfully!" : "Template created successfully!",
        error: (err) => err.message || `Failed to ${isEditing ? 'update' : 'create'} template`,
      })
      .then(() => {
        onClose();
        window.location.href = window.location.href;
      });
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div
        ref={modalRef}
        className="bg-white relative w-full max-w-md max-h-[90vh] rounded-2xl shadow-2xl transform transition-all duration-300 ease-out flex flex-col"
      >
        <div className="bg-[#4F22D8] px-6 py-4 text-white relative rounded-t-2xl flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="h-[30px] w-[30px] flex justify-center items-center bg-white/20 rounded-lg">
              <FileText className="w-5 h-5" />
            </div>
            <h2 className="text-lg font-semibold text-white">
              {isEditing ? "Edit Template" : "Create New Template"}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="absolute right-4 cursor-pointer top-4 p-2 hover:bg-white/20 rounded-lg transition-colors"
            aria-label="Close modal"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto min-h-0">
          <div className="p-6">
            <div className="mb-8">
              <div className="flex items-baseline gap-2 mb-4 pb-2 border-b border-gray-200">
                <div className="h-[30px] w-[30px] flex justify-center items-center bg-purple-100 rounded-lg">
                  <FileText className="w-4 h-4 text-[#4F22D8]" />
                </div>
                <h3 className="text-base font-semibold text-gray-900">
                  Basic Information
                </h3>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                    <Hash className="w-3.5 h-3.5 text-[#4F22D8]" />
                    Meta Template ID
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      className="w-full border h-[44px] border-gray-300 bg-white rounded-lg px-3 py-2.5 text-sm transition-all duration-200 outline-none"
                      placeholder="158841626"
                      value={templateId}
                      onChange={(e) => setTemplateId(e.target.value)}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                    <Type className="w-3.5 h-3.5 text-[#4F22D8]" />
                    Template Name
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      className="w-full h-[44px] border border-gray-300 bg-white rounded-lg px-3 py-2.5 text-sm transition-all duration-200 outline-none"
                      placeholder="Order Confirmation"
                      value={templateName}
                      onChange={(e) => setTemplateName(e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="mb-8">
              <div className="flex items-baseline gap-2 mb-4 pb-2 border-b border-gray-200">
                <div className="h-[30px] w-[30px] flex justify-center items-center bg-purple-100 rounded-lg">
                  <Settings className="w-4 h-4 text-[#4F22D8]" />
                </div>
                <h3 className="text-base font-semibold text-gray-900">
                  Template Configuration
                </h3>
              </div>
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-3">
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                    Template Type
                  </label>
                  <div className="space-y-2">
                    {[
                      {
                        value: "text",
                        label: "Text Template",
                        icon: FileText,
                        desc: "Simple text-based template",
                      },
                      {
                        value: "media",
                        label: "Media Template",
                        icon: Image,
                        desc: "Template with image/media",
                      },
                    ].map(({ value, label, icon: Icon, desc }) => (
                      <label
                        key={value}
                        className={`flex items-start gap-3 p-3 border-2 rounded-lg cursor-pointer transition-all duration-200 ${
                          type === value
                            ? "border-[#4F22D8] bg-indigo-50 text-[#4F22D8]"
                            : "border-gray-200 bg-white hover:border-gray-300"
                        }`}
                      >
                        <input
                          type="radio"
                          name="templateType"
                          value={value}
                          checked={type === value}
                          onChange={(e) => setType(e.target.value)}
                          className="!sr-only"
                        />
                        <div
                          className={`p-1 rounded-lg flex-shrink-0 ${
                            type === value ? "bg-indigo-100" : "bg-gray-100"
                          }`}
                        >
                          <Icon
                            className={`w-4 h-4 ${
                              type === value
                                ? "text-[#4F22D8]"
                                : "text-gray-500"
                            }`}
                          />
                        </div>
                        <div className="flex-1">
                          <div className="!text-sm font-medium">{label}</div>
                          <div className="text-xs text-gray-500 mt-0.5">
                            {desc}
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="space-y-3">
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                    Message Mode
                  </label>
                  <div className="space-y-2">
                    {[
                      {
                        value: "static",
                        label: "Static Message",
                        desc: "Fixed message content",
                      },
                      {
                        value: "dynamic",
                        label: "Dynamic Message",
                        desc: "Customizable with variables",
                      },
                    ].map(({ value, label, desc }) => (
                      <label
                        key={value}
                        className={`flex items-start gap-3 p-3 border-2 rounded-lg cursor-pointer transition-all duration-200 ${
                          messageMode === value
                            ? "border-[#4F22D8] bg-indigo-50 text-[#4F22D8]"
                            : "border-gray-200 bg-white hover:border-gray-300"
                        }`}
                      >
                        <input
                          type="radio"
                          name="messageMode"
                          value={value}
                          checked={messageMode === value}
                          onChange={(e) => setMessageMode(e.target.value)}
                          className="!sr-only"
                        />
                        <div
                          className={`w-4 h-4 rounded-full border-2 flex-shrink-0 mt-0.5 flex items-center justify-center ${
                            messageMode === value
                              ? "border-[#4F22D8] bg-[#4F22D8]"
                              : "border-gray-300"
                          }`}
                        >
                          {messageMode === value && (
                            <div className="w-1.5 h-1.5 bg-white rounded-full" />
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="text-sm font-medium">{label}</div>
                          <div className="text-xs text-gray-500 mt-0.5">
                            {desc}
                          </div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="mb-8">
              <div className="flex items-center gap-2 mb-4 pb-2 border-b border-gray-200">
                <div className="p-1.5 bg-green-100 rounded-lg">
                  <MessageSquare className="w-4 h-4 text-green-600" />
                </div>
                <h3 className="text-base font-semibold text-gray-900">
                  Sample Message
                </h3>
              </div>
              <div className="">
                <label className="text-sm font-medium text-gray-700">
                  Message Content
                </label>
                <textarea
                  className="w-full mt-2 border border-gray-300 bg-white rounded-lg px-3 py-2.5 text-sm transition-all duration-200 outline-none resize-none"
                  rows={4}
                  placeholder="Enter your template message here..."
                  value={sampleMessage}
                  onChange={(e) => setSampleMessage(e.target.value)}
                />
              </div>
            </div>

            {type === "media" && (
              <div className="mb-8">
                <div className="flex items-center gap-2 mb-4 pb-2 border-b border-gray-200">
                  <div className="p-1.5 bg-orange-100 rounded-lg">
                    <Image className="w-4 h-4 text-orange-600" />
                  </div>
                  <h3 className="text-base font-semibold text-gray-900">
                    Media Configuration
                  </h3>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">
                    Media URL
                  </label>
                  <input
                    type="url"
                    className="w-full border h-[44px] border-gray-300 bg-white rounded-lg px-3 py-2.5 text-sm transition-all duration-200 outline-none"
                    placeholder="https://example.com/image.jpg"
                    value={mediaUrl}
                    onChange={(e) => setMediaUrl(e.target.value)}
                  />
                </div>
                {mediaUrl && (
                  <div className="mt-3">
                    <p className="text-xs text-[#4F22D8] font-semibold mb-1 flex items-center gap-1">
                      <File size={16} color="#4F22D8" />
                      Preview:{" "}
                    </p>
                    <img
                      src={mediaUrl}
                      alt="Media Preview"
                      className="w-[120px] h-auto rounded-md border"
                    />
                  </div>
                )}
              </div>
            )}

            {messageMode === "dynamic" && (
              <div className="mb-8">
                <div className="flex items-center gap-2 mb-4 pb-2 border-b border-gray-200">
                  <div className="p-1.5 bg-yellow-100 rounded-lg">
                    <Hash className="w-4 h-4 text-yellow-600" />
                  </div>
                  <h3 className="text-base font-semibold text-gray-900">
                    Dynamic Placeholders
                  </h3>
                </div>
                <div className="space-y-3">
                  {placeholders.map((placeholder, index) => (
                    <div key={index} className="flex items-center gap-3">
                      <div className="flex-1">
                        <div className="text-black font-medium mb-2">
                          Position for label-{placeholder.position}
                        </div>
                        <div className="flex items-center gap-3">
                          <input
                            type="text"
                            className="w-full border h-[44px] border-gray-300 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 bg-white rounded-lg px-3 py-2.5 text-sm transition-all duration-200 outline-none"
                            placeholder=""
                            value={placeholder.label}
                            onChange={(e) =>
                              handlePlaceholderChange(index, e.target.value)
                            }
                          />
                          <button
                            onClick={() => handleRemovePlaceholder(index)}
                            className="p-2 text-red cursor-pointer hover:bg-red-50 rounded-lg transition-colors"
                            disabled={placeholders.length === 1}
                          >
                            <Minus className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                  <button
                    onClick={handleAddPlaceholder}
                    className="w-full flex h-[55px] items-center cursor-pointer justify-center gap-2 p-3 border-2 border-dashed border-gray-300 hover:border-[#4F22D8] hover:bg-indigo-50 rounded-lg transition-all duration-200 text-gray-600 hover:text-[#4F22D8]"
                  >
                    <Plus className="w-4 h-4" />
                    Add Placeholder
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="border-t rounded-b-2xl border-gray-200 px-6 py-4 bg-gray-50 flex-shrink-0">
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2.5 text-sm cursor-pointer font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              className="flex-1 px-4 py-2.5 text-sm cursor-pointer font-medium text-white bg-[#4F22D8] rounded-lg transition-all duration-200 shadow-sm hover:shadow-md"
            >
              {isEditing ? "Update Template" : "Create Template"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateTemplateModal;
